import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Answer, LearningTask } from "./learning_model";
import { LearningStore } from "./learning_store";
import { MarkdownPreview } from "../../core/ui/markdown/markdown_preview";
import "./learning.css";
import { LobbyScreen, HistoryScreen } from "./learning_lobby";
import { BuilderScreen } from "./learning_builder";
import { BankScreen } from "./learning_bank";
import { ProfileScreen } from "./learning_profile";
import { StatisticsPanel } from "./learning_statistics";
import { NavIcon, IconName } from "../../core/ui/navigation/nav_icon";
import { useSidebar } from "../../core/ui/navigation/use_sidebar";
import { PortalDialog } from "./portal_dialog";
export { LobbyScreen } from "./learning_lobby";

const LoginScreen = observer(({ store }: { store: LearningStore }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  return (
    <section className="portal-card portal-login">
      <h1>Вход в портал</h1>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          await store.login(username, password);
          setPassword("");
        }}
      >
        <label htmlFor="portal-username">Имя пользователя</label>
        <input
          id="portal-username"
          autoComplete="username"
          required
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          disabled={store.busy}
        />
        <label htmlFor="portal-password">Пароль</label>
        <input
          id="portal-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={store.busy}
        />
        <button type="submit" disabled={store.busy}>
          Войти
        </button>
      </form>
      <p className="portal-muted">
        Учётную запись предоставляет администратор.
      </p>
    </section>
  );
});

export function AnswerField({
  task,
  value,
  index,
  disabled,
  onChange,
}: {
  task: LearningTask;
  value: Answer;
  index: number;
  disabled: boolean;
  onChange: (answer: Answer) => void;
}) {
  if (task.type === "onlyText" || task.type === "onlyNumber") {
    return (
      <div>
        <label htmlFor={`answer-${index}`}>
          {task.type === "onlyNumber" ? "Числовой ответ" : "Текстовый ответ"}
        </label>
        <input
          id={`answer-${index}`}
          value={
            typeof value === "string" || typeof value === "number" ? value : ""
          }
          inputMode={task.type === "onlyNumber" ? "decimal" : "text"}
          placeholder="Введите ответ…"
          maxLength={10000}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    );
  }
  return (
    <fieldset className="portal-options" disabled={disabled}>
      <legend>
        {task.type === "oneOfSeveral"
          ? "Выберите один вариант"
          : "Выберите все верные варианты"}
      </legend>
      {task.options?.map((option, optionIndex) => (
        <label className="portal-option" key={optionIndex}>
          <input
            type={task.type === "oneOfSeveral" ? "radio" : "checkbox"}
            name={`answer-${index}`}
            checked={
              task.type === "oneOfSeveral"
                ? value === optionIndex
                : Array.isArray(value) && value.includes(optionIndex)
            }
            onChange={(event) => {
              if (task.type === "oneOfSeveral") onChange(optionIndex);
              else {
                const selected = Array.isArray(value) ? value : [];
                onChange(
                  event.target.checked
                    ? [...selected, optionIndex].sort((a, b) => a - b)
                    : selected.filter((i) => i !== optionIndex),
                );
              }
            }}
          />
          <span>{option}</span>
        </label>
      ))}
      <button
        disabled={disabled || value === null}
        onClick={() => onChange(null)}
      >
        Очистить ответ
      </button>
    </fieldset>
  );
}

function TaskResult({ task }: { task: LearningTask }) {
  if (!task.result) return null;
  const correctAnswers = task.result.correctAnswers.map((answer) =>
    task.options && typeof answer === "number"
      ? task.options[answer]
      : String(answer),
  );
  return (
    <section className="portal-result">
      <h3>
        Результат: {task.result.score} / {task.scoreAmount}
      </h3>
      <p>Правильные ответы: {correctAnswers.join("; ")}</p>
      {task.result.solution && (
        <>
          <h4>Разбор</h4>
          <MarkdownPreview
            value={task.result.solution}
            label="Разбор задания"
          />
        </>
      )}
    </section>
  );
}

export const AttemptScreen = observer(
  ({ store, go }: { store: LearningStore; go: (id?: string) => void }) => {
    const [selected, setSelected] = useState(0);
    const [confirmFinish, setConfirmFinish] = useState(false);
    const [, setClock] = useState(0);
    const heading = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
      if (!store.attempt?.expiresAt || store.attempt.status === "completed")
        return;
      const timer = window.setInterval(
        () => setClock((value) => value + 1),
        1000,
      );
      return () => window.clearInterval(timer);
    }, [store.attempt?.expiresAt, store.attempt?.status]);
    const attempt = store.attempt;
    if (!attempt) return null;
    const completed = attempt.status === "completed";
    const expired = !completed && store.isExpired();
    const hasAnswer = (answer: Answer) =>
      answer !== null &&
      answer !== "" &&
      (!Array.isArray(answer) || answer.length > 0);
    const answered = store.drafts.filter(hasAnswer).length;
    const task = attempt.tasks[selected];
    const remaining = attempt.expiresAt
      ? Math.max(
          0,
          Math.floor((Date.parse(attempt.expiresAt) - Date.now()) / 1000),
        )
      : 0;
    const time = `${Math.floor(remaining / 3600)}:${String(Math.floor(remaining / 60) % 60).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
    const navStart = Math.floor(selected / 30) * 30;
    function select(index: number) {
      setSelected(index);
      window.requestAnimationFrame(() => heading.current?.focus());
    }
    return (
      <>
        <header className="attempt-heading">
          <div>
            <button
              className="text-link"
              disabled={store.busy}
              onClick={() => go()}
            >
              ← К тестам
            </button>
            <h1>{attempt.title}</h1>
          </div>
          {!completed && attempt.expiresAt && (
            <div className="attempt-clock" aria-label="До окончания доступа">
              <NavIcon name="clock" />
              <time>{time}</time>
            </div>
          )}
          {completed && (
            <strong className="attempt-final-score">
              {attempt.score} / {attempt.maxScore} баллов
            </strong>
          )}
        </header>
        {expired && (
          <p role="status" className="portal-error">
            Срок действия теста истёк. Сохранённые ответы доступны для
            просмотра.
          </p>
        )}
        <div className="attempt-layout">
          {task && (
            <article className="portal-card question-card">
              <header className="question-heading">
                <h2 ref={heading} tabIndex={-1}>
                  Задание {selected + 1}
                </h2>
                <p className="portal-muted">{task.scoreAmount} баллов</p>
              </header>
              <div className="question-body">
                <h3>{task.title}</h3>
                <MarkdownPreview
                  value={task.description}
                  label={`Условие задания ${selected + 1}`}
                />
                <div className="question-answer">
                  <AnswerField
                    task={task}
                    value={store.drafts[selected]}
                    index={selected}
                    disabled={store.busy || completed || expired}
                    onChange={(value) => store.setAnswer(selected, value)}
                  />
                  {selected < attempt.tasks.length - 1 && (
                    <button
                      className="portal-primary"
                      disabled={store.busy}
                      onClick={async () => {
                        if (!completed && !expired) await store.save();
                        if (!store.error) select(selected + 1);
                      }}
                    >
                      Далее →
                    </button>
                  )}
                </div>
                <TaskResult task={task} />
              </div>
            </article>
          )}
          <aside className="portal-card task-navigator">
            <h2>Список задач</h2>
            {attempt.tasks.length <= 100 && (
              <p className="answer-counter">
                <strong>{answered}</strong> / {attempt.tasks.length} выполнено
              </p>
            )}
            <progress
              className="portal-progress"
              value={answered}
              max={attempt.tasks.length || 1}
              aria-label="Количество заданий с ответами"
            />
            <nav className="task-number-grid" aria-label="Переход к заданию">
              {attempt.tasks
                .slice(navStart, navStart + 30)
                .map((item, offset) => {
                  const index = navStart + offset;
                  return (
                    <button
                      key={index}
                      aria-label={`Задание ${index + 1}${hasAnswer(store.drafts[index]) ? ", ответ заполнен" : ""}`}
                      aria-current={selected === index ? "step" : undefined}
                      className={`${selected === index ? "selected" : ""} ${hasAnswer(store.drafts[index]) ? "answered" : ""}`}
                      onClick={() => select(index)}
                    >
                      {index + 1}
                    </button>
                  );
                })}
            </nav>
            {attempt.tasks.length > 30 && (
              <div className="portal-actions">
                <button
                  aria-label="Предыдущие задания"
                  disabled={!navStart}
                  onClick={() => select(navStart - 30)}
                >
                  ←
                </button>
                <button
                  aria-label="Следующие задания"
                  disabled={navStart + 30 >= attempt.tasks.length}
                  onClick={() => select(navStart + 30)}
                >
                  →
                </button>
              </div>
            )}
            {!completed && !expired && (
              <div className="navigator-actions">
                <button disabled={store.busy} onClick={() => store.save()}>
                  Сохранить ответы
                </button>
                <button
                  className="portal-primary"
                  disabled={store.busy}
                  onClick={() => setConfirmFinish(true)}
                >
                  Завершить работу
                </button>
                <p className="portal-muted" role="status">
                  {store.dirty
                    ? "Есть несохранённые ответы"
                    : "Ответы сохранены"}
                </p>
              </div>
            )}
          </aside>
        </div>
        {confirmFinish && !expired && (
          <PortalDialog
            title="Завершить попытку?"
            onClose={() => setConfirmFinish(false)}
          >
            <p>
              Ответы будут сохранены и проверены. После этого изменить их
              нельзя.
            </p>
            {answered < attempt.tasks.length && (
              <p>
                Без ответа: {attempt.tasks.length - answered}. Они дадут 0
                баллов.
              </p>
            )}
            <div className="portal-actions">
              <button onClick={() => setConfirmFinish(false)}>
                Продолжить решать
              </button>
              <button
                className="portal-primary"
                disabled={store.busy}
                onClick={async () => {
                  await store.save(true);
                  if (!store.error) setConfirmFinish(false);
                }}
              >
                Завершить и проверить
              </button>
            </div>
            {store.error && <p role="alert">{store.error}</p>}
          </PortalDialog>
        )}
      </>
    );
  },
);

const sections: { id: string; label: string; icon: IconName }[] = [
  { id: "tests", label: "Текущие тесты", icon: "tests" },
  { id: "build", label: "Конструктор", icon: "build" },
  { id: "variants", label: "Варианты", icon: "variants" },
  { id: "bank", label: "Банк заданий", icon: "bank" },
  { id: "statistics", label: "Статистика", icon: "statistics" },
];
function currentRoute() {
  return window.location.hash.slice(1) || "tests";
}
export const LearningScreen = observer(
  ({ onLeave }: { onLeave?: () => void }) => {
    const [store] = useState(() => new LearningStore());
    const [route, setRoute] = useState(currentRoute);
    const {
      open: menuOpen,
      setOpen: setMenuOpen,
      compact,
      sidebarRef,
    } = useSidebar();
    const [subjectNotice, setSubjectNotice] = useState(false);
    const routeRef = useRef(route);
    const attemptId = /^attempt\/([a-f\d]{24})$/i.exec(route)?.[1];
    const go = (id?: string) => {
      window.location.hash = id ? `attempt/${id}` : "tests";
    };
    useEffect(() => {
      store.init();
    }, [store]);
    useEffect(() => {
      const change = () => {
        const next = currentRoute();
        if (
          store.busy ||
          (store.dirty &&
            !window.confirm("Несохранённые ответы будут потеряны. Выйти?"))
        ) {
          window.history.replaceState(null, "", `#${routeRef.current}`);
          return;
        }
        if (!window.location.hash || window.location.hash === "#") {
          onLeave?.();
          return;
        }
        routeRef.current = next;
        setRoute(next);
        setMenuOpen(false);
      };
      const warn = (event: BeforeUnloadEvent) => {
        if (store.dirty || store.busy) {
          event.preventDefault();
          event.returnValue = "";
        }
      };
      window.addEventListener("hashchange", change);
      window.addEventListener("beforeunload", warn);
      return () => {
        window.removeEventListener("hashchange", change);
        window.removeEventListener("beforeunload", warn);
      };
    }, [store, onLeave, setMenuOpen]);
    useEffect(() => {
      if (!store.initialized || !store.user) return;
      if (attemptId) store.load(attemptId);
      else store.lobby();
    }, [store, store.initialized, store.user, attemptId]);
    const logout = async () => {
      if (
        store.dirty &&
        !window.confirm("Несохранённые ответы будут потеряны. Выйти?")
      )
        return;
      await store.logout();
      if (!store.user) go();
    };
    return (
      <div className={`learning-portal ${store.user ? "with-sidebar" : ""}`}>
        <header className="portal-header">
          <div className="portal-header-inner">
            {store.user && (
              <button
                className="menu-toggle"
                aria-label="Открыть меню"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <NavIcon name="menu" />
              </button>
            )}
            <a className="portal-brand" href="#tests">
              podvaltech
            </a>
            {store.user && (
              <a
                className="account-link"
                href="#profile"
                aria-label="Личный кабинет"
              >
                <NavIcon name="profile" />
                <span>Личный кабинет</span>
              </a>
            )}
          </div>
        </header>
        {store.user && (
          <>
            {menuOpen && (
              <button
                className="sidebar-backdrop"
                aria-label="Закрыть меню"
                onClick={() => setMenuOpen(false)}
              />
            )}
            <aside
              ref={sidebarRef}
              hidden={compact && !menuOpen}
              className={`portal-sidebar ${menuOpen ? "is-open" : ""}`}
              aria-label="Навигация портала"
            >
              <a className="sidebar-brand" href="#tests">
                podvaltech
              </a>
              <button
                className="subject-button"
                onClick={() => {
                  setMenuOpen(false);
                  setSubjectNotice(true);
                }}
              >
                <NavIcon name="subject" />
                Выберите предмет<span aria-hidden="true">⌄</span>
              </button>
              <nav aria-label="Разделы ученика">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    aria-current={route === section.id ? "page" : undefined}
                  >
                    <NavIcon name={section.icon} />
                    {section.label}
                  </a>
                ))}
              </nav>
              <div className="sidebar-account">
                <a
                  href="#profile"
                  aria-current={route === "profile" ? "page" : undefined}
                >
                  <NavIcon name="profile" />
                  {store.user.username}
                </a>
                <button disabled={store.busy} onClick={logout}>
                  <NavIcon name="logout" />
                  Выйти
                </button>
              </div>
            </aside>
          </>
        )}
        <main className="portal-main">
          {store.busy && (
            <p className="loading-status" role="status">
              Загрузка / сохранение…
            </p>
          )}
          {store.error && (
            <div className="portal-error" role="alert">
              {store.error}
              {store.user && (
                <button
                  disabled={store.busy}
                  onClick={() => {
                    if (
                      store.dirty &&
                      !window.confirm(
                        "Загрузить сохранённую версию? Несохранённые ответы будут потеряны.",
                      )
                    )
                      return;
                    if (attemptId) store.load(attemptId);
                    else store.lobby();
                  }}
                >
                  {store.conflict
                    ? "Открыть сохранённую попытку"
                    : "Повторить загрузку"}
                </button>
              )}
            </div>
          )}
          {store.notice &&
            (!attemptId || store.attempt?.status === "completed") && (
              <p className="portal-notice" role="status">
                {store.notice}
              </p>
            )}
          {store.initialized &&
            (!store.user ? (
              <LoginScreen store={store} />
            ) : attemptId ? (
              <AttemptScreen key={attemptId} store={store} go={go} />
            ) : route === "build" ? (
              <BuilderScreen store={store} go={go} />
            ) : route === "variants" ? (
              <LobbyScreen store={store} go={go} variants />
            ) : route === "bank" ? (
              <BankScreen store={store} go={go} />
            ) : route === "statistics" ? (
              <StatisticsPanel
                statistics={store.statistics}
                error={store.statisticsError}
                busy={store.busy}
                onRetry={() => store.refreshStatistics()}
                go={go}
                showAll={() => {
                  window.location.hash = "results";
                }}
              />
            ) : route === "results" ? (
              <HistoryScreen store={store} go={go} />
            ) : route === "profile" ? (
              <ProfileScreen store={store} />
            ) : (
              <LobbyScreen store={store} go={go} />
            ))}
        </main>
        {subjectNotice && (
          <PortalDialog
            title="Выбор предмета"
            onClose={() => setSubjectNotice(false)}
          >
            <p>Выбор предмета будет добавлен позже.</p>
            <button
              className="portal-primary"
              onClick={() => setSubjectNotice(false)}
            >
              Понятно
            </button>
          </PortalDialog>
        )}
      </div>
    );
  },
);
